import { useCallback, useEffect, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { CollapsibleSection } from "components/designV2/collapsibleSection/collapsibleSection";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Modal } from "components/designV2/modal/modal";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import { StackingZIndexContext } from "components/designV2/stacking/stackingZIndexContext";
import { ZIndex } from "config/zIndex";
import { useCloseOnEscape } from "hooks/useCloseOnEscape";
import { SalaryType } from "model/Salary";
import { useBodyScrollLock } from "web-apps/company/components/common/hooks/useBodyScrollLock";
import {
  getSelectedOptionLabel,
  STAFF_TAX_TABLE_OPTIONS,
} from "../shared/staffPanel.options.logic";
import { getEmploymentFieldVisibility } from "../shared/staffEmploymentFieldVisibility.logic";
import { createIntegerInputHandlers } from "../shared/staffNumericInput.logic";
import type {
  AddStaffFormData,
  AddStaffPanelProps,
} from "./addStaffPanel.contract";
import { useAddStaffPanelLogic } from "./addStaffPanel.logic";
import { toPaymentMethodHintTranslationKey } from "../shared/staff.paymentMethod.logic";
import {
  CloseButton,
  FieldsGrid,
  Footer,
  PhoneContinueButton,
  PhoneFieldsSpacer,
  HeaderRow,
  Overlay,
  Panel,
  PanelBody,
  SubmitErrorText,
  Title,
} from "./addStaffPanel.styled";

const toPaymentMethodTypeOrDefault = (
  type: string,
): AddStaffFormData["paymentMethodType"] => {
  if (type === "InternationalBankAccount") return "InternationalBankAccount";
  return "BankAccount";
};

export const AddStaffPanel = (
  props: AddStaffPanelProps,
): JSX.Element | null => {
  const { t } = useTranslation();
  const toMandatoryLabel = (label: string): string =>
    `${label} (${t("Staff.AddStaffPanelMandatory")})`;
  const onClose = props.onClose;
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);
  const logic = useAddStaffPanelLogic({
    isOpen: props.isOpen,
    onClose: props.onClose,
    onWorkerCreated: props.onWorkerCreated,
    unitTeamOptions: props.unitTeamOptions,
    t,
  });
  const { isSubmitting, setField } = logic;

  useBodyScrollLock(props.isOpen);

  useEffect(() => {
    if (props.isOpen) return;
    setIsDiscardConfirmOpen(false);
  }, [props.isOpen]);

  const closePanel = useCallback((): void => {
    setIsDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  const requestClose = useCallback((): void => {
    if (isSubmitting) return;
    if (logic.isFormStarted) {
      setIsDiscardConfirmOpen(true);
      return;
    }
    closePanel();
  }, [closePanel, isSubmitting, logic.isFormStarted]);

  useEffect(() => {
    if (!props.isOpen || !logic.isFormStarted) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [props.isOpen, logic.isFormStarted]);

  useCloseOnEscape({
    isOpen: props.isOpen && !isSubmitting && !isDiscardConfirmOpen,
    onClose: requestClose,
    stopPropagation: true,
    preventDefault: true,
  });

  const onOverlayClick = useCallback((): void => {
    requestClose();
  }, [requestClose]);

  const onPanelClick = useCallback((event: MouseEvent): void => {
    event.stopPropagation();
  }, []);

  const onSetField = useCallback(
    <K extends keyof AddStaffFormData>(
      field: K,
      value: AddStaffFormData[K],
    ): void => {
      setField(field, value);
    },
    [setField],
  );

  if (!props.isOpen) return null;

  const fieldVisibility = getEmploymentFieldVisibility(
    logic.formData.employmentType,
  );
  const salaryTypeLabel = getSelectedOptionLabel({
    options: logic.salaryOptions,
    value: logic.formData.salaryType,
  });
  const paymentTypeLabel = getSelectedOptionLabel({
    options: logic.paymentOptions,
    value: logic.formData.paymentType,
  });
  const salaryTypeFieldLabel = logic.validationRequirements
    .isSalaryModelRequired
    ? toMandatoryLabel(t("Staff.AddStaffPanelFieldSalaryModel"))
    : t("Staff.AddStaffPanelFieldSalaryModel");
  const paymentTypeFieldLabel = logic.validationRequirements
    .isPaymentTypeRequired
    ? toMandatoryLabel(t("Staff.AddStaffPanelFieldPaymentSolution"))
    : t("Staff.AddStaffPanelFieldPaymentSolution");
  const noticeDaysHandlers = createIntegerInputHandlers(
    logic.formData.noticeDays,
    (value) => onSetField("noticeDays", value),
  );
  const vacationDaysHandlers = createIntegerInputHandlers(
    logic.formData.vacationDays,
    (value) => onSetField("vacationDays", value),
  );

  return (
    <StackingZIndexContext.Provider value={ZIndex.sidePanel}>
      <Overlay onClick={onOverlayClick}>
        <Panel onClick={onPanelClick}>
          <HeaderRow>
            <Title>{t("Staff.AddStaffPanelTitle")}</Title>
            <CloseButton
              iconName="v2/cross"
              size="small"
              aria-label={t("DesignComponents.CloseModal")}
              disabled={isSubmitting}
              onClick={requestClose}
            />
          </HeaderRow>

          <PanelBody>
            <CollapsibleSection
              label={t("Staff.AddStaffPanelSectionPersonalDetails")}
              defaultOpen
            >
              <FieldsGrid>
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldPhoneNumberMandatory")}
                  hintText={t("Staff.AddStaffPanelHintPhoneNumberCountryCode")}
                  maxLength={15}
                  value={logic.formData.phoneNumber}
                  errorText={logic.phoneError}
                  hasError={logic.phoneError.length > 0}
                  onChange={logic.onPhoneNumberChange}
                  onClear={() => logic.onPhoneNumberChange("")}
                  onBlur={logic.onPhoneNumberBlur}
                />
                <PhoneContinueButton
                  label={t("General.Continue")}
                  size="small"
                  disabled={
                    logic.isSubmitting ||
                    logic.isCheckingPhoneNumber ||
                    logic.formData.phoneNumber.trim().length === 0
                  }
                  isLoading={logic.isCheckingPhoneNumber}
                  onClick={logic.onPhoneNumberContinue}
                />
                <PhoneFieldsSpacer />
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldFirstNameMandatory")}
                  value={logic.formData.firstName}
                  disabled={logic.areFormFieldsDisabled}
                  hasError={logic.firstNameError.length > 0}
                  errorText={logic.firstNameError}
                  onChange={(value) => onSetField("firstName", value)}
                />
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldLastNameMandatory")}
                  value={logic.formData.lastName}
                  disabled={logic.areFormFieldsDisabled}
                  hasError={logic.lastNameError.length > 0}
                  errorText={logic.lastNameError}
                  onChange={(value) => onSetField("lastName", value)}
                />
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldEmail")}
                  value={logic.formData.email}
                  type="email"
                  disabled={logic.areFormFieldsDisabled}
                  hasError={logic.emailError.length > 0}
                  errorText={logic.emailError}
                  onChange={(value) => onSetField("email", value)}
                />
                <InputField
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldPersonalNumber")}
                  hintText={t("Staff.AddStaffPanelHintPersonalNumberFormat")}
                  value={logic.formData.personalIdentityNumber}
                  disabled={logic.areFormFieldsDisabled}
                  hasError={logic.personalIdentityNumberError.length > 0}
                  errorText={logic.personalIdentityNumberError}
                  onChange={(value) =>
                    onSetField("personalIdentityNumber", value)
                  }
                />
                <SearchableDropdown
                  fullWidth
                  label={toMandatoryLabel(t("Staff.AddStaffPanelFieldTeam"))}
                  options={logic.teamOptions}
                  selectedValues={logic.formData.teamIds}
                  multiSelect
                  disabled={logic.areFormFieldsDisabled}
                  onSelectedValuesChange={(value) =>
                    onSetField("teamIds", value)
                  }
                  placeholder={t("Staff.AddStaffPanelPlaceholderNotAssigned")}
                />
              </FieldsGrid>
            </CollapsibleSection>

            <CollapsibleSection
              label={t("Staff.AddStaffPanelSectionEmployment")}
              defaultOpen
            >
              <FieldsGrid>
                <Dropdown
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldTemplate")}
                  options={[
                    {
                      value: "",
                      label: t("Staff.AddStaffPanelTemplateCustom"),
                    },
                    ...logic.employmentTemplates.map((template) => ({
                      value: String(template.id),
                      label: template.name ?? "",
                    })),
                  ]}
                  value={logic.selectedEmploymentTemplateId}
                  disabled={
                    logic.areFormFieldsDisabled ||
                    logic.isLoadingEmploymentTemplates ||
                    logic.employmentTemplates.length === 0
                  }
                  onChange={logic.applyEmploymentTemplate}
                />
                <Dropdown
                  fullWidth
                  label={t("Staff.AddStaffPanelFieldTemplateSalary")}
                  placeholder={t(
                    "Staff.AddStaffPanelPlaceholderTemplateSalary",
                  )}
                  options={[
                    {
                      value: "",
                      label: t("Staff.AddStaffPanelTemplateCustom"),
                    },
                    ...logic.templateSalaries.map((salary) => ({
                      value: String(salary.id),
                      label: salary.name ?? "",
                    })),
                  ]}
                  value={logic.selectedTemplateSalaryId}
                  disabled={
                    logic.areFormFieldsDisabled ||
                    logic.templateSalaries.length === 0
                  }
                  onChange={logic.applyEmploymentTemplateSalary}
                />
                <Dropdown
                  fullWidth
                  label={toMandatoryLabel(
                    t("Staff.AddStaffPanelFieldEmploymentType"),
                  )}
                  options={logic.employmentOptions}
                  value={logic.formData.employmentType}
                  disabled={logic.areFormFieldsDisabled}
                  onChange={(value) => onSetField("employmentType", value)}
                  placeholder={t(
                    "Staff.AddStaffPanelPlaceholderEmploymentType",
                  )}
                />
                <InputField
                  fullWidth
                  label={
                    logic.validationRequirements.isEmployeeNumberRequired
                      ? toMandatoryLabel(
                          t("Staff.AddStaffPanelFieldEmployeeNumber"),
                        )
                      : t("Staff.AddStaffPanelFieldEmployeeNumber")
                  }
                  value={logic.formData.employeeNumber}
                  hasError={logic.employeeNumberError.length > 0}
                  errorText={logic.employeeNumberError}
                  iconName="v2/reload"
                  iconSize="18px"
                  iconAriaLabel={t(
                    "Staff.AddStaffPanelActionReloadEmployeeNumber",
                  )}
                  onIconClick={logic.fetchEmployeeNumber}
                  disabled={
                    logic.isLoadingEmployeeNumber ||
                    logic.isSubmitting ||
                    logic.areFormFieldsDisabled
                  }
                  onChange={(value) => onSetField("employeeNumber", value)}
                />
                <InputField
                  fullWidth
                  label={toMandatoryLabel(t("Staff.AddStaffPanelFieldRole"))}
                  value={logic.formData.role}
                  disabled={logic.areFormFieldsDisabled}
                  hasError={logic.roleError.length > 0}
                  errorText={logic.roleError}
                  onChange={(value) => onSetField("role", value)}
                />
                {fieldVisibility.showSalaryType &&
                  (logic.salaryOptions.length === 1 ? (
                    <InputField
                      fullWidth
                      label={salaryTypeFieldLabel}
                      value={salaryTypeLabel}
                      disabled
                    />
                  ) : (
                    <Dropdown
                      fullWidth
                      label={salaryTypeFieldLabel}
                      options={logic.salaryOptions}
                      value={logic.formData.salaryType}
                      disabled={logic.areFormFieldsDisabled}
                      onChange={(value) => onSetField("salaryType", value)}
                      placeholder={t(
                        "Staff.AddStaffPanelPlaceholderSalaryModel",
                      )}
                    />
                  ))}
                {fieldVisibility.showSalary && (
                  <InputField
                    fullWidth
                    type="number"
                    label={
                      logic.validationRequirements.isSalaryAmountRequired
                        ? toMandatoryLabel(logic.salaryAmountLabel)
                        : logic.salaryAmountLabel
                    }
                    value={logic.formData.salaryAmount}
                    placeholder={t(
                      "Staff.AddStaffPanelPlaceholderSalaryAmount",
                    )}
                    disabled={logic.areFormFieldsDisabled}
                    onChange={(value) => onSetField("salaryAmount", value)}
                  />
                )}
                {fieldVisibility.showSalaryType &&
                  logic.formData.salaryType === SalaryType.Monthly && (
                    <InputField
                      fullWidth
                      type="number"
                      label={
                        logic.validationRequirements.isWorkPercentageRequired
                          ? toMandatoryLabel(
                              `${t("Staff.AddStaffPanelFieldWorkingHours")} (%)`,
                            )
                          : `${t("Staff.AddStaffPanelFieldWorkingHours")} (%)`
                      }
                      value={logic.formData.workPercentage}
                      disabled={logic.areFormFieldsDisabled}
                      hasError={logic.workPercentageError.length > 0}
                      errorText={logic.workPercentageError}
                      onChange={(value) => onSetField("workPercentage", value)}
                    />
                  )}
                {fieldVisibility.showTaxTable && (
                  <Dropdown
                    fullWidth
                    label={
                      logic.validationRequirements.isTaxTableRequired
                        ? toMandatoryLabel(
                            t("Staff.AddStaffPanelFieldTaxTable"),
                          )
                        : t("Staff.AddStaffPanelFieldTaxTable")
                    }
                    options={STAFF_TAX_TABLE_OPTIONS}
                    value={logic.formData.taxTable}
                    disabled={logic.areFormFieldsDisabled}
                    onChange={(value) => onSetField("taxTable", value)}
                  />
                )}
                {fieldVisibility.showPaymentType &&
                  (logic.paymentOptions.length === 1 ? (
                    <InputField
                      fullWidth
                      label={paymentTypeFieldLabel}
                      value={paymentTypeLabel}
                      disabled
                    />
                  ) : (
                    <Dropdown
                      fullWidth
                      label={paymentTypeFieldLabel}
                      options={logic.paymentOptions}
                      value={logic.formData.paymentType}
                      disabled={logic.areFormFieldsDisabled}
                      onChange={(value) => onSetField("paymentType", value)}
                    />
                  ))}
                {fieldVisibility.showEmployeeCategory && (
                  <Dropdown
                    fullWidth
                    label={
                      logic.validationRequirements.isEmployeeCategoryRequired
                        ? toMandatoryLabel(
                            t("Staff.AddStaffPanelFieldStaffType"),
                          )
                        : t("Staff.AddStaffPanelFieldStaffType")
                    }
                    options={logic.employeeCategoryOptions}
                    value={logic.formData.employeeCategory}
                    disabled={logic.areFormFieldsDisabled}
                    onChange={(value) => onSetField("employeeCategory", value)}
                  />
                )}
                <Dropdown
                  fullWidth
                  label={t("Staff.PaymentMethodType")}
                  options={logic.paymentMethodOptions}
                  value={logic.formData.paymentMethodType}
                  disabled={logic.areFormFieldsDisabled}
                  onChange={(value) =>
                    onSetField(
                      "paymentMethodType",
                      toPaymentMethodTypeOrDefault(value),
                    )
                  }
                />
                <InputField
                  fullWidth
                  label={t("Staff.PaymentMethodValue")}
                  value={logic.formData.paymentMethodValue}
                  disabled={logic.areFormFieldsDisabled}
                  hasError={logic.paymentMethodError.length > 0}
                  hintText={t(
                    toPaymentMethodHintTranslationKey(
                      logic.formData.paymentMethodType,
                    ),
                  )}
                  errorText={logic.paymentMethodError}
                  onChange={(value) => onSetField("paymentMethodValue", value)}
                />
                <DatePicker
                  label={toMandatoryLabel(
                    t("Staff.AddStaffPanelFieldStartDate"),
                  )}
                  value={logic.formData.startDate}
                  disabled={logic.areFormFieldsDisabled}
                  onChange={(value) => onSetField("startDate", value)}
                  allowClear
                />
                <DatePicker
                  label={
                    logic.validationRequirements.isEndDateRequired
                      ? toMandatoryLabel(t("Staff.AddStaffPanelFieldEndDate"))
                      : t("Staff.AddStaffPanelFieldEndDate")
                  }
                  value={logic.formData.endDate}
                  disabled={logic.areFormFieldsDisabled}
                  onChange={(value) => onSetField("endDate", value)}
                  allowClear
                />
              </FieldsGrid>
            </CollapsibleSection>

            {fieldVisibility.showContractTerms && (
              <CollapsibleSection
                label={t("Staff.AddStaffPanelSectionAgreementsTerms")}
                defaultOpen
              >
                <FieldsGrid>
                  <InputField
                    fullWidth
                    label={t(
                      "Staff.AddStaffPanelFieldCollectiveSalaryAgreement",
                    )}
                    value={logic.formData.agreement}
                    disabled={logic.areFormFieldsDisabled}
                    hasError={logic.agreementError.length > 0}
                    errorText={logic.agreementError}
                    onChange={(value) => onSetField("agreement", value)}
                  />
                  <InputField
                    fullWidth
                    type="number"
                    label={t("Staff.AddStaffPanelFieldNoticePeriodDays")}
                    value={logic.formData.noticeDays}
                    disabled={logic.areFormFieldsDisabled}
                    hasError={logic.noticeDaysError.length > 0}
                    errorText={logic.noticeDaysError}
                    onChange={noticeDaysHandlers.onChange}
                    onBlur={noticeDaysHandlers.onBlur}
                  />
                  <InputField
                    fullWidth
                    type="number"
                    label={t("Staff.AddStaffPanelFieldVacationDays")}
                    value={logic.formData.vacationDays}
                    disabled={logic.areFormFieldsDisabled}
                    hasError={logic.vacationDaysError.length > 0}
                    errorText={logic.vacationDaysError}
                    onChange={vacationDaysHandlers.onChange}
                    onBlur={vacationDaysHandlers.onBlur}
                  />
                  <InputField
                    fullWidth
                    label={t("Staff.AddStaffPanelFieldWorkPlace")}
                    value={logic.formData.workplace}
                    disabled={logic.areFormFieldsDisabled}
                    hasError={logic.workplaceError.length > 0}
                    errorText={logic.workplaceError}
                    onChange={(value) => onSetField("workplace", value)}
                  />
                </FieldsGrid>
              </CollapsibleSection>
            )}
          </PanelBody>

          <Footer>
            {logic.submitError.length > 0 ? (
              <SubmitErrorText role="alert">
                {logic.submitError}
              </SubmitErrorText>
            ) : null}
            <PrimaryButton
              label={t("Staff.AddStaffPanelActionAddWorker")}
              disabled={logic.isCreateDisabled}
              isLoading={logic.isSubmitting}
              onClick={logic.onAddWorkerClick}
            />
          </Footer>
        </Panel>
      </Overlay>
      <Modal
        variant="confirm"
        label={t("Staff.AddStaffPanelDiscardConfirmTitle")}
        size="compact"
        showCloseButton={false}
        isOpen={isDiscardConfirmOpen}
        onClose={() => setIsDiscardConfirmOpen(false)}
        footer={
          <>
            <SecondaryButton
              label={t("General.Cancel")}
              onClick={() => setIsDiscardConfirmOpen(false)}
            />
            <PrimaryButton
              label={t("Staff.AddStaffPanelDiscardConfirmAction")}
              onClick={closePanel}
            />
          </>
        }
      >
        {t("Staff.AddStaffPanelDiscardConfirmBody")}
      </Modal>
    </StackingZIndexContext.Provider>
  );
};
